// Pure aggregation/formatting helpers for CargoVarietiesChart. Kept free of React/recharts so
// the component file stays Fast-Refresh-safe (a component module must not also export plain
// functions) and so these helpers can be exercised without a DOM.

const TOP_N_VARIETIES = 10;

const normalizeCountry = (country) => String(country || '').trim().toUpperCase();

/** Aliases that collapse to one canonical destination identity, keyed by the letters-only form. */
const CANONICAL_DESTINATION_ALIASES = {
  USA: 'USA',
  US: 'USA',
  UNITEDSTATES: 'USA',
  UNITEDSTATESOFAMERICA: 'USA',
  EEUU: 'USA',
  ESTADOSUNIDOS: 'USA',
  CANADA: 'CANADA',
  CA: 'CANADA',
};

/**
 * Canonical destination identity for a raw destination_country value. Collapses spelling/casing/
 * punctuation variants that refer to the same country (e.g. "U.S.A." and "Usa" both from live data)
 * into one key, so they don't render as separate destination segments/textures. Unlike
 * `normalizeCountry`, this strips all non-letter characters, so use it only for destination IDENTITY
 * (aggregation grouping, ordering, series keys) — never for the destination FILTER, which stays keyed
 * on `normalizeCountry` per existing behaviour. Falls back to the full `normalizeCountry` form when
 * nothing letters-only survives the strip (e.g. a non-Latin-script or symbols-only value), so that
 * destination still gets its own identity instead of silently losing its volume from every
 * destination-keyed total (segments, split strip/legend, tooltip breakdown).
 */
const canonicalDestinationKey = (country) => {
  const normalized = normalizeCountry(country);
  // Fold diacritics first (e.g. "MÉXICO" -> "MEXICO") so accented and unaccented spellings of the
  // same country collapse to one identity, then strip everything but letters.
  const folded = normalized.normalize('NFD').replace(/[̀-ͯ]/g, ''); // Unicode Combining Diacritical Marks block (U+0300–U+036F)
  const stripped = folded.replace(/[^A-Z]/g, '');
  return CANONICAL_DESTINATION_ALIASES[stripped] || stripped || normalized;
};

/** Display label for a normalized key: keep 2-3 letter all-caps codes (USA, US, UK) as is, title-case the rest. */
const formatDestinationLabel = (key) => {
  if (!key) return '';
  if (/^[A-Z]{2,3}$/.test(key)) return key;
  return key
    .toLowerCase()
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

/**
 * Human label for a canonical destination key. USA/Canada always render with their fixed English
 * label regardless of raw spelling; everything else is title-cased from the first-seen RAW spelling
 * (via `normalizeCountry`, spaces preserved) so a multi-word country like "COSTA RICA" doesn't get
 * mangled into the letters-only identity key ("COSTARICA" -> "Costarica").
 */
const labelForCanonicalKey = (key, rawForm) => {
  if (key === 'USA') return 'USA';
  if (key === 'CANADA') return 'Canada';
  return formatDestinationLabel(rawForm || key);
};

/**
 * Global destination order across both coasts: canonical destination key + display label,
 * ordered by combined (east+west) metric value desc, tie-broken alphabetically by key.
 * Texture (solid vs hatched) is assigned by this order so the same destination
 * always gets the same texture on every panel.
 */
const computeDestinationOrder = (varieties, metric) => {
  const sums = new Map();
  const rawByKey = new Map();
  (varieties || []).forEach((v) => {
    const key = canonicalDestinationKey(v.destination_country);
    if (!key) return;
    if (!rawByKey.has(key)) rawByKey.set(key, normalizeCountry(v.destination_country));
    const value = (v.east_coast?.[metric] || 0) + (v.west_coast?.[metric] || 0);
    sums.set(key, (sums.get(key) || 0) + value);
  });
  return [...sums.entries()]
    .filter(([, sum]) => sum > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => ({ key, label: labelForCanonicalKey(key, rawByKey.get(key)) }));
};

/**
 * Group origin+destination-filtered rows into one row per variety for a single coast,
 * splitting each row by destination country. Backend rows are keyed by
 * (origin, destination_country, variety), so grouping by variety guarantees one bar per
 * variety per coast instead of one bar per (origin, destination) combination.
 */
const aggregateByVariety = (varieties, coastKey, metric, destinationOrder, limit = TOP_N_VARIETIES) => {
  if (!varieties) return { rows: [], destinationSeries: [], isSplit: false };

  const destIndex = new Map(destinationOrder.map((entry, i) => [entry.key, i]));
  const destLabel = new Map(destinationOrder.map((entry) => [entry.key, entry.label]));
  const groups = new Map();

  varieties.forEach((v) => {
    const varietyName = String(v.variety || '').trim();
    const coastData = v[coastKey] || {};
    const value = coastData[metric] || 0;
    const rowPorts = coastData.ports || {};

    if (!groups.has(varietyName)) {
      groups.set(varietyName, {
        variety: varietyName,
        type_of_variety: '',
        typeValue: -Infinity,
        value: 0,
        ports: {},
        destinations: new Map(),
      });
    }
    const group = groups.get(varietyName);
    group.value += value;
    if (value > group.typeValue) {
      group.typeValue = value;
      group.type_of_variety = v.type_of_variety || '';
    }
    Object.entries(rowPorts).forEach(([port, vals]) => {
      const { boxes = 0, weight_kg = 0 } = vals || {};
      if (!group.ports[port]) group.ports[port] = { boxes: 0, weight_kg: 0 };
      group.ports[port].boxes += boxes;
      group.ports[port].weight_kg += weight_kg;
    });

    const dk = canonicalDestinationKey(v.destination_country);
    if (!dk) return;
    if (!group.destinations.has(dk)) group.destinations.set(dk, { value: 0, ports: {} });
    const destEntry = group.destinations.get(dk);
    destEntry.value += value;
    Object.entries(rowPorts).forEach(([port, vals]) => {
      const { boxes = 0, weight_kg = 0 } = vals || {};
      if (!destEntry.ports[port]) destEntry.ports[port] = { boxes: 0, weight_kg: 0 };
      destEntry.ports[port].boxes += boxes;
      destEntry.ports[port].weight_kg += weight_kg;
    });
  });

  // Coast totals per destination across ALL grouped rows (not just the top N) so the
  // summary line and legend reconcile with the coast total even when some varieties
  // fall outside the top-N bars.
  const coastDestTotals = new Map();
  groups.forEach((group) => {
    group.destinations.forEach((destEntry, dk) => {
      coastDestTotals.set(dk, (coastDestTotals.get(dk) || 0) + destEntry.value);
    });
  });
  const coastTotal = [...coastDestTotals.values()].reduce((a, b) => a + b, 0);

  const destinationSeries = destinationOrder
    .filter(({ key }) => (coastDestTotals.get(key) || 0) > 0)
    .map(({ key, label }) => {
      const value = coastDestTotals.get(key) || 0;
      return {
        key,
        label,
        index: destIndex.get(key),
        value,
        pct: coastTotal > 0 ? (value / coastTotal) * 100 : 0,
      };
    });

  const isSplit = destinationSeries.length >= 2;

  const rows = [...groups.values()]
    .filter((g) => g.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
    .map((g) => {
      const rowDestinations = destinationOrder
        .filter(({ key }) => (g.destinations.get(key)?.value || 0) > 0)
        .map(({ key }) => {
          const d = g.destinations.get(key);
          return {
            key,
            label: destLabel.get(key) || formatDestinationLabel(key),
            value: d.value,
            pct: g.value > 0 ? (d.value / g.value) * 100 : 0,
            ports: d.ports,
            index: destIndex.get(key),
          };
        });

      // Keyed by GLOBAL destination INDEX (not the destination key/name) so the field name
      // is always a safe recharts `dataKey` / object-property name regardless of what
      // characters the raw destination_country value contains (see canonicalDestinationKey's
      // normalizeCountry fallback). CoastPanel's stacked <Bar dataKey> reads these by index too.
      const segFields = {};
      destinationSeries.forEach((s) => {
        segFields[`seg_${s.index}`] = g.destinations.get(s.key)?.value || 0;
      });

      return {
        variety: g.variety,
        type_of_variety: g.type_of_variety,
        value: g.value,
        ports: g.ports,
        destinations: rowDestinations,
        ...segFields,
      };
    });

  return { rows, destinationSeries, isSplit };
};

export {
  normalizeCountry,
  canonicalDestinationKey,
  computeDestinationOrder,
  aggregateByVariety,
};
